/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState, useMemo } from 'react';
import { ApolloError } from '@apollo/client/v4-migration';
import { useLazyQuery } from '@apollo/client/react';
import moment from 'moment';
import AuditLogGrid from './grid';
import AuditSearchBox from './search';
import { getSearchConditions } from './service';
import { useLocalState, useBoolean, useTranslation } from 'hooks';
import { SEARCH_AUDIT_LOGS } from 'queries/audit';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import { searchAuditLogs, searchAuditLogsVariables } from '@queries/generated/audit.generated';
import { AuditLogFields } from '@queries/generated/fragments.generated';
import './audit-log.scss';

const AuditLog = () => {
    const { t } = useTranslation();

    const { state: localState } = useLocalState();
    const isFromSearchBox = useBoolean(false);

    const [searchLogs, { data, error, loading }] = useLazyQuery<searchAuditLogs, searchAuditLogsVariables>(
        SEARCH_AUDIT_LOGS,
    );

    const [logItems, setLogItems] = useState<AuditLogFields[]>([]);
    const [filteredLogItems, setFilteredLogItems] = useState<AuditLogFields[]>([]);

    const loadingState = useBoolean(false);

    const doSearch = (search: any, isFromSearchBoxValue = false) => {
        const conditions = getSearchConditions(search);

        if (!conditions.length) {
            alert('You must select at least 1 condition');
            return;
        }

        if (isFromSearchBoxValue) isFromSearchBox.setTrue();

        // eslint-disable-next-line no-console
        searchLogs({ variables: { conditions } }).catch((err) => console.error(err));
    };

    // Do an initial search on page load
    useEffect(() => {
        doSearch({
            startDate: moment().subtract(5, 'day').toDate(),
            endDate: null,
        });
    }, [localState?.selectedOrganisation]);

    useEffect(() => {
        if (!data?.searchAuditLogs) return;
        const sortedAuditLogs = [...(data?.searchAuditLogs ?? [])].sort(
            (a, b) => Number(a?.created) - Number(b?.created),
        ) as AuditLogFields[];
        if (!isFromSearchBox.value) {
            setFilteredLogItems(sortedAuditLogs);
            setLogItems(sortedAuditLogs);
        } else {
            setFilteredLogItems(sortedAuditLogs);
        }
    }, [data]);

    const studies = useMemo(
        () => localState?.selectedOrganisation?.studies ?? [],
        [localState?.selectedOrganisation?.studies],
    );

    if (error) return <Error error={error} />;

    return (
        <>
            <PageTitle text={t('auditLog:auditLog')} />
            <AuditSearchBox
                onSearch={doSearch}
                selectedOrganisation={localState?.selectedOrganisation}
                setLoading={loadingState.setValue}
                userStudies={localState?.userStudies}
            />
            {error && <div>Error: {(error as ApolloError).message}</div>}
            <br />
            <br />
            {(loading || loadingState.value) && <Loading float />}
            <AuditLogGrid
                items={logItems}
                userStudies={localState?.userStudies}
                filteredLogItems={filteredLogItems}
                orgId={localState?.selectedStudy?.organisation.id}
                studies={studies}
            />
        </>
    );
};

export default React.memo(AuditLog);
